import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { IHomepageMetricGroup } from '@skystone/shared';
import { metricGroupApi } from '@/services/metric-group-api';
import { Spinner } from '@/components/ui';
import { ChevronRightIcon } from '@/components/ui/icons';
import { MetricGroupCard } from './MetricGroupCard';

export function MetricGroupsSection() {
  const [groups, setGroups] = useState<IHomepageMetricGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    metricGroupApi
      .getAll()
      .then((data) => {
        if (cancelled) return;
        const visible = data
          .filter((g) => g.visible)
          .sort((a, b) => a.displayOrder - b.displayOrder);
        setGroups(visible);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load metric groups.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            What we measure
          </p>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            Our metric groups. One source of truth.
          </h2>
        </div>
        <Link
          to="/metric-catalogue"
          className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          See full catalogue
          <ChevronRightIcon className="h-4 w-4" />
        </Link>
      </div>

      {error && <p className="text-sm text-muted-foreground">{error}</p>}

      {!error && groups === null && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Loading…
        </div>
      )}

      {!error && groups !== null && groups.length === 0 && (
        <p className="text-sm text-muted-foreground">No metric groups configured.</p>
      )}

      {!error && groups !== null && groups.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {groups.map((g) => (
            <MetricGroupCard key={g.groupKey} group={g} />
          ))}
        </div>
      )}
    </section>
  );
}
